import React, { useMemo } from 'react';
import classNames from 'classnames';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getDataRoomRoute } from 'utils';
import { NEW_WINDOW_PROPS } from 'data/global';
import LinkToIcon from 'icons/LinkTo.icon';
import DataRoomNodePreview from './components/DataRoomNodePreview';
import Counter from 'components/Counter';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';
import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';

import './PortfolioDataRoomNodes.styles.scss';

const TOO_MANY_DOCUMENTS = 5;

type PortfolioDataRoomNodesProps = {
  className?: string;
  dataRoomId: string;
  dataRoomNodes: DataRoomNode[];
  dataRoomName: string;
  ownerId: string;
  userSide: QAndASides;
};

const PortfolioDataRoomNodes: React.FC<PortfolioDataRoomNodesProps> = (
  props,
) => {
  const {
    className,
    dataRoomId,
    dataRoomName,
    ownerId,
    userSide,
    dataRoomNodes = [],
  } = props;

  const classes = classNames('portfolio-data-room-nodes', className);

  const { t } = useTranslation();

  const numberOfDocuments = useMemo(
    () => dataRoomNodes.length,
    [dataRoomNodes],
  );

  const hasTooManyDocuments = useMemo(
    () => numberOfDocuments > TOO_MANY_DOCUMENTS,
    [numberOfDocuments],
  );

  const RenderMessage = useMemo(() => {
    return (
      <p>
        {!numberOfDocuments
          ? t('documentMessageForNoDocuments')
          : t(
              hasTooManyDocuments
                ? 'documentMessageForTooManyDocuments'
                : 'documentsMessage',
              { count: numberOfDocuments },
            )}
      </p>
    );
  }, [numberOfDocuments, hasTooManyDocuments, t]);

  const RenderDocs = useMemo(() => {
    if (!numberOfDocuments) return null;

    if (hasTooManyDocuments)
      return (
        <Link
          className="ncoded-button ncoded-button--solid"
          to={getDataRoomRoute(dataRoomId)}
          {...NEW_WINDOW_PROPS}
        >
          <LinkToIcon />
          {t('dataRoom')}
        </Link>
      );

    return (
      <div className="container">
        {dataRoomNodes?.map((node) => (
          <DataRoomNodePreview
            key={node._id}
            node={node}
            dataRoomName={dataRoomName}
            ownerId={ownerId}
            userSide={userSide}
          />
        ))}
      </div>
    );
  }, [
    numberOfDocuments,
    hasTooManyDocuments,
    dataRoomId,
    dataRoomNodes,
    dataRoomName,
    ownerId,
    userSide,
    t,
  ]);

  return (
    <div className={classes}>
      <div className="heading">
        <p>{t('dataRoom')}</p>
        <Counter count={numberOfDocuments} variant={COUNTER_VARIANTS.GREY} />
      </div>

      {RenderMessage}
      {RenderDocs}
    </div>
  );
};

export default PortfolioDataRoomNodes;
